import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

function Section({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <h2 className="font-heading text-lg font-bold mb-3">{title}</h2>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">{label}</label>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm" />
    </div>
  );
}

export default function Settings() {
  const [smtp, setSmtp] = useState({});
  const [wa, setWa] = useState({});
  const [tpl, setTpl] = useState({});
  const [tplMode, setTplMode] = useState("manual");
  const [company, setCompany] = useState({});
  const [ab, setAb] = useState({});
  const [abTesting, setAbTesting] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetting, setResetting] = useState(false);
  const [keepMasters, setKeepMasters] = useState(false);
  const [testWaTo, setTestWaTo] = useState("");
  const [testSmtpTo, setTestSmtpTo] = useState("");
  const [testingWa, setTestingWa] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);

  const testSend = async (channel, to, setLoad) => {
    if (!to) { toast.error("Enter a destination first"); return; }
    setLoad(true);
    try {
      const { data } = await api.post("/admin/test-send", { channel, to });
      if (data.success) toast.success(`Test ${channel} sent to ${to}`);
      else toast.error(`Failed: ${(data.error || "unknown").slice(0, 160)}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoad(false); }
  };

  useEffect(() => {
    (async () => {
      const [s, w, t, c, a] = await Promise.all([
        api.get("/settings/smtp"), api.get("/settings/whatsapp"),
        api.get("/settings/templates"), api.get("/settings/company"),
        api.get("/settings/alignbooks").catch(() => ({ data: {} })),
      ]);
      setSmtp(s.data || {}); setWa(w.data || {}); setTpl(t.data || {}); setCompany(c.data || {}); setAb(a.data || {});
    })();
  }, []);

  const save = async (cat, data) => {
    try {
      await api.put(`/settings/${cat}`, data); toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="page-settings" className="space-y-5">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Configuration</div>
        <h1 className="font-heading text-3xl font-extrabold text-slate-900 mt-1">Settings</h1>
      </div>

      <Section title="Company Profile">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Company Name" value={company.company_name} onChange={(v) => setCompany({ ...company, company_name: v })} />
          <Field label="Brand" value={company.brand} onChange={(v) => setCompany({ ...company, brand: v })} />
          <Field label="Address" value={company.address} onChange={(v) => setCompany({ ...company, address: v })} />
          <Field label="Website" value={company.website} onChange={(v) => setCompany({ ...company, website: v })} />
          <Field label="Default Email" value={company.default_email} onChange={(v) => setCompany({ ...company, default_email: v })} />
        </div>
        <div className="mt-3 text-right"><button data-testid="btn-save-company" onClick={() => save("company", company)} className="bg-[var(--navy)] text-white text-sm px-4 py-2 rounded-md">Save Company</button></div>
      </Section>

      {showReset && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50" onClick={() => setShowReset(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg p-6 w-full max-w-md" data-testid="dialog-reset">
            <h3 className="font-heading text-xl font-bold text-rose-800">Confirm Data Reset</h3>
            <p className="text-sm text-slate-600 mt-2">
              This will permanently delete all business data on <b>this environment</b>. Type <code className="bg-slate-100 px-1 rounded">RESET</code> to confirm.
            </p>
            <input
              data-testid="reset-confirm-input"
              value={resetConfirm}
              onChange={(e) => setResetConfirm(e.target.value)}
              placeholder="Type RESET"
              className="w-full mt-4 px-3 py-2 border border-slate-300 rounded-md text-sm font-mono"
              autoFocus
            />
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => { setShowReset(false); setResetConfirm(""); }} className="px-4 py-2 text-sm border border-slate-300 rounded-md">Cancel</button>
              <button
                data-testid="btn-confirm-reset"
                disabled={resetConfirm !== "RESET" || resetting}
                onClick={async () => {
                  setResetting(true);
                  try {
                    const { data } = await api.post("/admin/reset-data", { confirm: "RESET", keep_masters: keepMasters, keep_settings: true });
                    toast.success(`Reset done · ${Object.entries(data.cleared).filter(([, v]) => v).map(([k, v]) => `${k}:${v}`).join(" · ")}`);
                    setShowReset(false); setResetConfirm("");
                  } catch (e) { toast.error(formatApiError(e)); }
                  finally { setResetting(false); }
                }}
                className="px-4 py-2 text-sm bg-rose-700 hover:bg-rose-800 text-white rounded-md disabled:opacity-50"
              >{resetting ? "Resetting…" : "Reset Data"}</button>
            </div>
          </div>
        </div>
      )}

      <Section title="SMTP / Email">
        <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-md text-xs text-slate-700">
          <div className="font-semibold text-sm mb-1">Zoho Mail quick config</div>
          <div>Host: <code>smtp.zoho.in</code> (India) or <code>smtp.zoho.com</code> (global) · Port: <code>587</code> · TLS: on · Username: your full Zoho email · Password: create an <b>App Password</b> from <a className="text-amber-700 underline" href="https://accounts.zoho.com/home#security/app_password" target="_blank" rel="noreferrer">Zoho Security → App Passwords</a> (NOT your login password)</div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="SMTP Host" value={smtp.host} onChange={(v) => setSmtp({ ...smtp, host: v })} />
          <Field label="Port" value={smtp.port} onChange={(v) => setSmtp({ ...smtp, port: v })} type="number" />
          <Field label="Username" value={smtp.username} onChange={(v) => setSmtp({ ...smtp, username: v })} />
          <Field label="App Password" value={smtp.password} onChange={(v) => setSmtp({ ...smtp, password: v })} type="password" />
          <Field label="Sender Name" value={smtp.sender_name} onChange={(v) => setSmtp({ ...smtp, sender_name: v })} />
          <Field label="Sender Email" value={smtp.sender_email} onChange={(v) => setSmtp({ ...smtp, sender_email: v })} />
          <Field label="Reply-To" value={smtp.reply_to} onChange={(v) => setSmtp({ ...smtp, reply_to: v })} />
          <div className="flex items-center gap-2 pt-6"><input type="checkbox" checked={smtp.use_tls !== false} onChange={(e) => setSmtp({ ...smtp, use_tls: e.target.checked })} /> <span className="text-sm">Use TLS (STARTTLS)</span></div>

          <div className="col-span-2">
            <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">Always CC on every client email</label>
            <div className="mt-2 space-y-2">
              {(() => {
                const raw = smtp.cc_emails;
                const list = Array.isArray(raw)
                  ? raw
                  : (typeof raw === "string" ? raw.split(/[,\n]/).map((s) => s.trim()).filter(Boolean) : []);
                const rows = list.length ? list : [""];
                const update = (nl) => setSmtp({ ...smtp, cc_emails: nl });
                return (
                  <>
                    {rows.map((v, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <input
                          data-testid={`smtp-cc-${i}`}
                          type="email"
                          value={v}
                          onChange={(e) => { const nl = [...rows]; nl[i] = e.target.value; update(nl.filter((x, idx) => idx === i || x)); }}
                          placeholder="name@procurmart.com"
                          className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm font-mono"
                        />
                        <button
                          type="button"
                          data-testid={`smtp-cc-remove-${i}`}
                          onClick={() => { const nl = rows.filter((_, idx) => idx !== i); update(nl); }}
                          className="text-xs text-rose-600 border border-rose-200 rounded-md px-2 py-1 hover:bg-rose-50"
                          disabled={rows.length === 1 && !rows[0]}
                        >Remove</button>
                      </div>
                    ))}
                    <button
                      type="button"
                      data-testid="btn-add-cc"
                      onClick={() => update([...rows, ""])}
                      className="text-xs text-slate-700 border border-slate-300 rounded-md px-3 py-1.5 hover:bg-slate-50"
                    >+ Add another CC</button>
                  </>
                );
              })()}
            </div>
            <div className="text-xs text-slate-500 mt-2">Every dispatch email — new or resent — will also be CC'd to these SRAD addresses. Currently CC'd: <b>{(() => {
              const raw = smtp.cc_emails;
              const list = Array.isArray(raw) ? raw : (typeof raw === "string" ? raw.split(/[,\n]/).map((s) => s.trim()).filter(Boolean) : []);
              return list.length ? list.length : 0;
            })()} address(es)</b>. Leave the box empty to disable CC.</div>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button data-testid="btn-preset-zoho-in" onClick={() => setSmtp({ ...smtp, host: "smtp.zoho.in", port: 587, use_tls: true })} className="text-xs text-slate-600 border border-slate-300 rounded-full px-3 py-1.5">Use Zoho India preset</button>
            <input
              data-testid="smtp-test-to"
              type="email"
              value={testSmtpTo}
              onChange={(e) => setTestSmtpTo(e.target.value)}
              placeholder="you@example.com"
              className="px-3 py-2 border border-slate-300 rounded-md text-sm w-56"
            />
            <button
              data-testid="btn-test-smtp"
              disabled={testingSmtp}
              onClick={() => testSend("email", testSmtpTo, setTestingSmtp)}
              className="bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold px-4 py-2 rounded-md disabled:opacity-60"
            >{testingSmtp ? "Sending…" : "Test Send"}</button>
          </div>
          <button data-testid="btn-save-smtp" onClick={() => save("smtp", smtp)} className="bg-[var(--navy)] text-white text-sm px-4 py-2 rounded-md">Save SMTP</button>
        </div>
      </Section>

      <Section title="WhatsApp (Meta Cloud API)">
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-900 space-y-1">
          <div className="font-semibold text-sm">How to go live in 30 seconds</div>
          <div>1. Meta Developer Console → your App → <b>WhatsApp → API Setup</b></div>
          <div>2. Copy <b>Access Token</b> (starts with <code>EAA…</code>, click the eye icon to reveal)</div>
          <div>3. Copy <b>Phone number ID</b> (15-16 digit number — <i>not</i> the phone number itself)</div>
          <div>4. If you have an <b>approved template</b>, fill Template Name + Language + Placeholder Order. Otherwise leave those blank and we'll send free-text (only works within a 24-hour customer session).</div>
          <div className="mt-2 text-emerald-900 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
            <b>Attachments are auto-sent:</b> any documents you tick during a dispatch are uploaded to WhatsApp media and delivered as document/image messages after the text/template — no extra config needed.
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Access Token (EAA…)" value={wa.access_token} onChange={(v) => setWa({ ...wa, access_token: v })} />
          <Field label="Phone Number ID" value={wa.phone_number_id} onChange={(v) => setWa({ ...wa, phone_number_id: v })} />
          <Field label="Business Account ID (optional)" value={wa.business_account_id} onChange={(v) => setWa({ ...wa, business_account_id: v })} />
          <div />
          <div className="col-span-2 border-t border-slate-200 pt-3 mt-1">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Approved Template (optional — for compliant outbound)</div>
          </div>
          <Field label="Template Name (e.g. dispatch_notification)" value={wa.template_name} onChange={(v) => setWa({ ...wa, template_name: v })} />
          <Field label="Template Language (e.g. en_US)" value={wa.template_language} onChange={(v) => setWa({ ...wa, template_language: v })} />
          <div className="col-span-2">
            <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">Placeholder Order (comma-separated)</label>
            <input
              data-testid="wa-template-order"
              value={(wa.template_placeholder_order || []).join(", ")}
              onChange={(e) => setWa({ ...wa, template_placeholder_order: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
              placeholder="ContactPerson, PONumber, InvoiceNumber, Transporter, LRNumber, DispatchDate, ExpectedDelivery, TrackingURL"
              className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm font-mono"
            />
            <div className="text-xs text-slate-500 mt-1">Map each Meta placeholder <code>{"{{1}} {{2}}…"}</code> to a field name. Available: ContactPerson, PONumber, InvoiceNumber, InvoiceDate, InvoiceAmount, Transporter, LRNumber, DispatchDate, ExpectedDelivery, TrackingURL, ClientName</div>
          </div>
          <div className="col-span-2 mt-3 border border-slate-200 rounded-md p-3 bg-slate-50">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Ready-to-copy body for Meta template approval</div>
            <pre className="text-xs font-mono text-slate-700 whitespace-pre-wrap bg-white border border-slate-200 rounded p-3">{`Dear {{1}},
Your material against PO No. {{2}} has been dispatched.
Invoice No.: {{3}}
Transporter: {{4}}
LR / Tracking No.: {{5}}
Dispatch Date: {{6}}
Expected Delivery: {{7}}
Track: {{8}}

Regards,
SRAD SERVICES PRIVATE LIMITED - ProcurMart`}</pre>
            <div className="text-xs text-slate-500 mt-2">
              <b>Category:</b> Utility · <b>Language:</b> English (en) · <b>Suggested name:</b> <code>dispatch_notification</code>. Submit in Meta Business Manager → WhatsApp Manager → Message Templates → Create. Once approved, paste the exact template name above and set placeholder order to <code>ContactPerson, PONumber, InvoiceNumber, Transporter, LRNumber, DispatchDate, ExpectedDelivery, TrackingURL</code>.
            </div>
            <button
              type="button"
              data-testid="btn-copy-template"
              onClick={() => {
                const body = `Dear {{1}},\nYour material against PO No. {{2}} has been dispatched.\nInvoice No.: {{3}}\nTransporter: {{4}}\nLR / Tracking No.: {{5}}\nDispatch Date: {{6}}\nExpected Delivery: {{7}}\nTrack: {{8}}\n\nRegards,\nSRAD SERVICES PRIVATE LIMITED - ProcurMart`;
                navigator.clipboard.writeText(body); toast.success("Template body copied");
              }}
              className="mt-2 text-xs bg-white border border-slate-300 rounded-full px-3 py-1"
            >Copy template body</button>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <input
              data-testid="wa-test-to"
              value={testWaTo}
              onChange={(e) => setTestWaTo(e.target.value)}
              placeholder="+91 98xxxxxxxx"
              className="px-3 py-2 border border-slate-300 rounded-md text-sm font-mono w-56"
            />
            <button
              data-testid="btn-test-whatsapp"
              disabled={testingWa}
              onClick={() => testSend("whatsapp", testWaTo, setTestingWa)}
              className="bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold px-4 py-2 rounded-md disabled:opacity-60"
            >{testingWa ? "Sending…" : "Test Send"}</button>
          </div>
          <button data-testid="btn-save-whatsapp" onClick={() => save("whatsapp", wa)} className="bg-[var(--navy)] text-white text-sm px-4 py-2 rounded-md">Save WhatsApp</button>
        </div>
      </Section>

      <Section title="Message Templates">
        <div className="flex items-center gap-2 mb-3">
          <div className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">Mode</div>
          <div className="inline-flex rounded-md border border-slate-300 overflow-hidden">
            <button
              type="button"
              data-testid="tpl-mode-default"
              onClick={async () => {
                if (!window.confirm("Reset all templates to ProcurMart defaults? Any manual edits will be replaced.")) return;
                try {
                  const { data } = await api.post("/settings/templates/reset");
                  setTpl(data.templates || {});
                  setTplMode("default");
                  toast.success("Templates reset to default");
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              className={`px-3 py-1.5 text-xs font-semibold ${tplMode === "default" ? "bg-[var(--navy)] text-white" : "bg-white text-slate-700"}`}
            >Set Default</button>
            <button
              type="button"
              data-testid="tpl-mode-manual"
              onClick={() => setTplMode("manual")}
              className={`px-3 py-1.5 text-xs font-semibold border-l border-slate-300 ${tplMode === "manual" ? "bg-[var(--navy)] text-white" : "bg-white text-slate-700"}`}
            >Manual</button>
          </div>
          <span className="text-xs text-slate-500 ml-2">
            {tplMode === "default"
              ? "Templates use ProcurMart defaults (read-only). Click Manual to customize."
              : "Custom templates — your edits will be saved when you click Save Templates."}
          </span>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">WhatsApp Template</label>
            <textarea data-testid="tpl-whatsapp" rows={8} value={tpl.whatsapp_template || ""} onChange={(e) => setTpl({ ...tpl, whatsapp_template: e.target.value })} readOnly={tplMode === "default"} className={`w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm font-mono ${tplMode === "default" ? "bg-slate-50 text-slate-600" : ""}`} />
          </div>
          <div>
            <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">Email Subject</label>
            <input data-testid="tpl-email-subject" value={tpl.email_subject || ""} onChange={(e) => setTpl({ ...tpl, email_subject: e.target.value })} readOnly={tplMode === "default"} className={`w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm ${tplMode === "default" ? "bg-slate-50 text-slate-600" : ""}`} />
          </div>
          <div>
            <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">Email Body</label>
            <textarea data-testid="tpl-email" rows={12} value={tpl.email_body || ""} onChange={(e) => setTpl({ ...tpl, email_body: e.target.value })} readOnly={tplMode === "default"} className={`w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm font-mono ${tplMode === "default" ? "bg-slate-50 text-slate-600" : ""}`} />
          </div>
          <div className="text-xs text-slate-500">Placeholders: {"{{ContactPerson}} {{ClientName}} {{DispatchCode}} {{PONumber}} {{InvoiceNumber}} {{InvoiceDate}} {{InvoiceAmount}} {{Transporter}} {{LRNumber}} {{DispatchDate}} {{ExpectedDelivery}} {{TrackingURL}} {{TransporterTrackingURL}}"}</div>
        </div>
        <div className="mt-3 text-right">
          <button
            data-testid="btn-save-templates"
            disabled={tplMode === "default"}
            onClick={() => save("templates", tpl)}
            className="bg-[var(--navy)] text-white text-sm px-4 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >Save Templates</button>
        </div>
      </Section>

      <Section title="AlignBooks Integration">
        <div className="p-3 bg-sky-50 border border-sky-200 rounded-md text-xs text-sky-900 mb-3">
          Pull invoice data (Invoice #, Amount incl. GST, Client, PO, Date, Branch) directly from AlignBooks so your operators don't re-key it. Once configured, a <b>Pull from AlignBooks</b> button appears on the New Dispatch screen.
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-2 flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="ab-enabled" checked={!!ab.enabled} onChange={(e) => setAb({ ...ab, enabled: e.target.checked })} />
            Enable AlignBooks integration
          </label>
          <Field label="Base URL (e.g. https://api.alignbooks.com)" value={ab.base_url} onChange={(v) => setAb({ ...ab, base_url: v })} />
          <Field label="Invoices Endpoint Path" value={ab.invoices_path || "/api/invoices"} onChange={(v) => setAb({ ...ab, invoices_path: v })} />
          <Field label="API Key / Token" value={ab.api_key} onChange={(v) => setAb({ ...ab, api_key: v })} />
          <Field label="Company / Tenant ID (optional)" value={ab.company_id} onChange={(v) => setAb({ ...ab, company_id: v })} />
          <Field label="Auth Header Name" value={ab.auth_header || "Authorization"} onChange={(v) => setAb({ ...ab, auth_header: v })} />
          <Field label="Auth Prefix (e.g. Bearer)" value={ab.auth_prefix ?? "Bearer"} onChange={(v) => setAb({ ...ab, auth_prefix: v })} />
        </div>
        <div className="mt-4">
          <div className="text-[11px] uppercase font-semibold tracking-widest text-slate-500 mb-2">Field Mapping (JSON keys from AlignBooks response)</div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Invoice Number key" value={ab.field_invoice_number || "invoice_number"} onChange={(v) => setAb({ ...ab, field_invoice_number: v })} />
            <Field label="Invoice Date key" value={ab.field_invoice_date || "invoice_date"} onChange={(v) => setAb({ ...ab, field_invoice_date: v })} />
            <Field label="Invoice Amount (incl GST) key" value={ab.field_invoice_amount || "amount_including_gst"} onChange={(v) => setAb({ ...ab, field_invoice_amount: v })} />
            <Field label="Client Name key" value={ab.field_client_name || "client_name"} onChange={(v) => setAb({ ...ab, field_client_name: v })} />
            <Field label="Client PO Number key" value={ab.field_client_po || "po_number"} onChange={(v) => setAb({ ...ab, field_client_po: v })} />
            <Field label="Branch / Location key" value={ab.field_branch || "branch"} onChange={(v) => setAb({ ...ab, field_branch: v })} />
            <Field label="External Invoice ID key" value={ab.field_invoice_id || "id"} onChange={(v) => setAb({ ...ab, field_invoice_id: v })} />
          </div>
        </div>
        <div className="mt-3 flex justify-end gap-2">
          <button
            data-testid="btn-test-alignbooks"
            disabled={abTesting || !ab.base_url}
            onClick={async () => {
              setAbTesting(true);
              try {
                const { data } = await api.post("/integrations/alignbooks/test");
                if (data.ok) toast.success(`AlignBooks reachable · HTTP ${data.status_code}`);
                else toast.error(`Test failed · ${data.error || "HTTP " + data.status_code}`);
              } catch (e) { toast.error(formatApiError(e)); }
              finally { setAbTesting(false); }
            }}
            className="text-sm border border-slate-300 px-4 py-2 rounded-md disabled:opacity-50"
          >{abTesting ? "Testing…" : "Test Connection"}</button>
          <button data-testid="btn-save-alignbooks" onClick={() => save("alignbooks", ab)} className="bg-[var(--navy)] text-white text-sm px-4 py-2 rounded-md">Save AlignBooks</button>
        </div>
      </Section>

      <Section title="Danger Zone">
        <div className="p-3 bg-rose-50 border border-rose-200 rounded-md text-sm text-rose-900 mb-3">
          <div className="font-semibold">Reset Test Data</div>
          <div className="text-xs mt-1">Deletes every dispatch, document, communication, follow-up, audit log, and non-admin user. Keeps the super admin account and your settings. Auto codes (SRAD-CL / SRAD-TR / SRAD-DSP) restart from 1. Use before going live for real.</div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={keepMasters} onChange={(e) => setKeepMasters(e.target.checked)} data-testid="reset-keep-masters" />
            Keep Clients / Contacts / Transporters (only wipe transactions)
          </label>
          <button
            data-testid="btn-open-reset"
            onClick={() => setShowReset(true)}
            className="ml-auto bg-rose-700 hover:bg-rose-800 text-white text-sm font-semibold px-4 py-2 rounded-md"
          >Reset Test Data…</button>
        </div>
      </Section>
    </div>
  );
}
